import React, {useState, useEffect} from 'react';
import SEO from '../../Components/Common/SEO';
import PropertyCard from '../../Components/Common/PropertyCard';
import {
    Search,
    MapPin,
    Building,
    Users,
    Shield,
    Star,
    Eye,
    CheckCircle,
    Home as HomeIcon,
    ArrowRight,
    Loader,
    AlertCircle,
    Bed,
    Bath,
    Square,
    Heart,
    Calendar,
    UserPlus,
    Crown,
    ChevronLeft,
    ChevronRight,
    Play,
    Pause,
} from 'lucide-react';
import {API_CONSTANTS} from "../../Constants";
import {useNavigate} from "react-router-dom";

const Home = () => {
    const navigate = useNavigate();

    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const [isAutoPlay, setIsAutoPlay] = useState(true);

    // State management
    const [featuredProperties, setFeaturedProperties] = useState([]);
    const [popularLocations, setPopularLocations] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [stats, setStats] = useState([]);
    const [howItWorks, setHowItWorks] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Loading states
    const [isLoadingProperties, setIsLoadingProperties] = useState(true);
    const [isLoadingLocations, setIsLoadingLocations] = useState(true);
    const [isLoadingTestimonials, setIsLoadingTestimonials] = useState(true);
    const [isLoadingStats, setIsLoadingStats] = useState(true);

    // Error states
    const [errors, setErrors] = useState({});

    // Default static data for how it works
    const defaultHowItWorks = [
        {
            step: 1,
            title: "Search & Browse",
            description: "Use our advanced search to find properties that match your criteria.",
            icon: Search
        },
        {
            step: 2,
            title: "Schedule Viewing",
            description: "Book virtual or in-person tours at your convenience.",
            icon: Eye
        },
        {
            step: 3,
            title: "Apply Online",
            description: "Submit your application and documents securely online.",
            icon: CheckCircle
        },
        {
            step: 4,
            title: "Move In",
            description: "Get approved and move into your new home hassle-free.",
            icon: HomeIcon
        }
    ];

    // Fetch featured properties
    useEffect(() => {
        const fetchFeaturedProperties = async () => {
            try {
                setIsLoadingProperties(true);
                const response = await fetch(API_CONSTANTS.BASE_URL + '/featured/featured', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(localStorage.getItem('authToken') && {
                            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                        })
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch featured properties');
                }

                const data = await response.json();

                console.log("Featured properties: ");
                console.log(data);

                if (response.ok) {
                    setFeaturedProperties(data.properties);
                } else {
                    throw new Error(data.message || 'Failed to fetch featured properties');
                }
            } catch (error) {
                console.error('Error fetching featured properties:', error);
                setErrors(prev => ({
                    ...prev,
                    properties: 'Failed to load featured properties'
                }));

                // Fallback to original beautiful data
                setFeaturedProperties([
                    {
                        id: 1,
                        title: "Modern 2BR Apartment in Westlands",
                        location: "Westlands, Nairobi",
                        price: "KES 85,000",
                        priceType: "per month",
                        image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                        bedrooms: 2,
                        bathrooms: 2,
                        area: "120 sqm",
                        rating: 4.8,
                        reviews: 24,
                        featured: true,
                        available: "Available Now"
                    },
                    {
                        id: 2,
                        title: "Luxury 3BR Penthouse in Karen",
                        location: "Karen, Nairobi",
                        price: "KES 150,000",
                        priceType: "per month",
                        image: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                        bedrooms: 3,
                        bathrooms: 3,
                        area: "200 sqm",
                        rating: 4.9,
                        reviews: 18,
                        featured: true,
                        available: "Available Dec 1st"
                    },
                    {
                        id: 3,
                        title: "Cozy 1BR Studio in Kilimani",
                        location: "Kilimani, Nairobi",
                        price: "KES 45,000",
                        priceType: "per month",
                        image: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                        bedrooms: 1,
                        bathrooms: 1,
                        area: "60 sqm",
                        rating: 4.6,
                        reviews: 32,
                        featured: true,
                        available: "Available Now"
                    }
                ]);
            } finally {
                setIsLoadingProperties(false);
            }
        };

        fetchFeaturedProperties();
    }, []);

    // Fetch testimonials
    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                setIsLoadingTestimonials(true);
                const response = await fetch(API_CONSTANTS.BASE_URL + '/testimonials', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch testimonials');
                }

                const data = await response.json();

                if (data.success) {
                    setTestimonials(data.data.testimonials || []);
                } else {
                    throw new Error(data.message || 'Failed to fetch testimonials');
                }
            } catch (error) {
                console.error('Error fetching testimonials:', error);
                setErrors(prev => ({
                    ...prev,
                    testimonials: 'Failed to load testimonials'
                }));

                // Fallback to original beautiful data
                setTestimonials([
                    {
                        name: "Sarah Johnson",
                        role: "Marketing Manager",
                        content: "Found my dream apartment in just 2 days! The process was seamless and the property manager was incredibly helpful.",
                        rating: 5,
                        image: "https://images.unsplash.com/photo-1494790108755-2616b612b8c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
                    },
                    {
                        name: "David Kimani",
                        role: "Software Engineer",
                        content: "Best rental platform in Kenya. Great selection of verified properties and transparent pricing.",
                        rating: 5,
                        image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
                    },
                    {
                        name: "Grace Wanjiku",
                        role: "Teacher",
                        content: "The virtual tours saved me so much time. I was able to shortlist properties before visiting in person.",
                        rating: 5,
                        image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
                    }
                ]);
            } finally {
                setIsLoadingTestimonials(false);
            }
        };

        fetchTestimonials();
    }, []);

    // Fetch platform statistics
    useEffect(() => {
        const fetchStats = async () => {
            try {
                setIsLoadingStats(true);
                const response = await fetch(API_CONSTANTS.BASE_URL + '/stats/platform', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch platform stats');
                }

                const data = await response.json();

                if (data.success) {
                    const apiStats = data.data.stats || {};
                    console.log("apiStats")
                    console.log(apiStats)
                    setStats([
                        {label: "Active Listings", value: apiStats.activeListings, icon: Building},
                        {label: "Happy Tenants", value: apiStats.happyTenants, icon: Users},
                        {label: "Verified Properties", value: apiStats.verifiedProperties, icon: Shield},
                        {label: "Average Rating", value: apiStats.averageRating, icon: Star}
                    ]);
                } else {
                    throw new Error(data.message || 'Failed to fetch platform stats');
                }
            } catch (error) {
                console.error('Error fetching platform stats:', error);
                setErrors(prev => ({
                    ...prev,
                    stats: 'Failed to load platform statistics'
                }));

                // Fallback so the stats row still renders something
                setStats([
                    {label: "Active Listings", value: "10,000+", icon: Building},
                    {label: "Happy Tenants", value: "50,000+", icon: Users},
                    {label: "Verified Properties", value: "100%", icon: Shield},
                    {label: "Average Rating", value: "4.8", icon: Star}
                ]);
            } finally {
                setIsLoadingStats(false);
            }
        };

        fetchStats();
    }, []);

    // Set how it works (static data)
    useEffect(() => {
        setHowItWorks(defaultHowItWorks);
    }, []);

    // Auto-play effect
    useEffect(() => {
        if (isAutoPlay && testimonials && testimonials.length > 1) {
            const interval = setInterval(() => {
                setCurrentTestimonial(prev =>
                    prev === testimonials.length - 1 ? 0 : prev + 1
                );
            }, 8000); // 8 seconds for better reading time

            return () => clearInterval(interval);
        }
    }, [isAutoPlay, testimonials]);

    // Replace your existing popular locations useEffect with this enhanced version

    useEffect(() => {
        const fetchPopularLocations = async () => {
            try {
                setIsLoadingLocations(true);
                const response = await fetch(API_CONSTANTS.BASE_URL + '/locations/popular', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch popular locations');
                }

                const data = await response.json();


                console.log("location");
                console.log(data);

                if (response.ok) {

                    // Transform API data and add images
                    const transformedLocations = await Promise.all(
                        (data.popularLocations).map(async (location) => {
                            // Generate a beautiful image for the location
                            const locationImage = await getLocationImage(location.name, location.city);

                            return {
                                name: location.name,
                                city: location.city,
                                neighborhoodId: location.neighborhoodId,
                                propertyCount: location.propertyCount || 0,
                                totalViews: location.totalViews || 0,
                                totalInquiries: location.totalInquiries || 0,
                                overallScore: location.overallScore || 0,
                                popularityScore: location.popularityScore || 0,
                                properties: formatPropertyCount(location.propertyCount),
                                image: locationImage,
                                // Calculate engagement metrics
                                engagement: calculateEngagement(location.totalViews, location.totalInquiries),
                                scoreGrade: getScoreGrade(location.overallScore)
                            };
                        })
                    );

                    // Sort by popularity score and overall score
                    const sortedLocations = transformedLocations.sort((a, b) => {
                        if (b.popularityScore !== a.popularityScore) {
                            return b.popularityScore - a.popularityScore;
                        }
                        return b.overallScore - a.overallScore;
                    });

                    setPopularLocations(sortedLocations);
                } else {
                    throw new Error(data.message || 'Failed to fetch popular locations');
                }
            } catch (error) {
                console.error('Error fetching popular locations:', error);
                setErrors(prev => ({
                    ...prev,
                    locations: 'Failed to load popular locations'
                }));

                // Enhanced fallback data
                setPopularLocations([
                    {
                        name: "Westlands",
                        city: "Nairobi",
                        properties: "1,200+ properties",
                        propertyCount: 1200,
                        overallScore: 8.5,
                        scoreGrade: "Excellent",
                        image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    },
                    {
                        name: "Karen",
                        city: "Nairobi",
                        properties: "800+ properties",
                        propertyCount: 800,
                        overallScore: 9.2,
                        scoreGrade: "Premium",
                        image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    },
                    {
                        name: "Kilimani",
                        city: "Nairobi",
                        properties: "950+ properties",
                        propertyCount: 950,
                        overallScore: 8.1,
                        scoreGrade: "Great",
                        image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    },
                    {
                        name: "Lavington",
                        city: "Nairobi",
                        properties: "600+ properties",
                        propertyCount: 600,
                        overallScore: 8.7,
                        scoreGrade: "Excellent",
                        image: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    },
                    {
                        name: "Runda",
                        city: "Nairobi",
                        properties: "400+ properties",
                        propertyCount: 400,
                        overallScore: 9.0,
                        scoreGrade: "Premium",
                        image: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    },
                    {
                        name: "Parklands",
                        city: "Nairobi",
                        properties: "750+ properties",
                        propertyCount: 750,
                        overallScore: 7.8,
                        scoreGrade: "Good",
                        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    }
                ]);
            } finally {
                setIsLoadingLocations(false);
            }
        };

        fetchPopularLocations();
    }, []);

// Helper Functions
    const getLocationImage = async (locationName, city) => {
        // Create a mapping of Nairobi neighborhoods to appropriate Unsplash images
        const locationImageMap = {
            'Karen': 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'Westlands': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'Kilimani': 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'Lavington': 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'Runda': 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'Parklands': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'Kileleshwa': 'https://images.unsplash.com/photo-1605146769289-440113cc3d00?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'Upperhill': 'https://images.unsplash.com/photo-1577495508048-b635879837f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'Spring Valley': 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'Muthaiga': 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
        };

        // Return mapped image or generate a fallback
        return locationImageMap[locationName] || generateFallbackImage(locationName);
    };

    const generateFallbackImage = (locationName) => {
        // Curated Unsplash collection for modern urban/residential areas
        const fallbackImages = [
            'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
            'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
        ];

        // Use location name to consistently pick same image
        const hash = locationName.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
        return fallbackImages[hash % fallbackImages.length];
    };

    const formatPropertyCount = (count) => {
        if (!count || count === 0) return "No properties";
        if (count === 1) return "1 property";
        if (count < 100) return `${count} properties`;
        if (count < 1000) return `${count}+ properties`;
        return `${Math.floor(count / 100) * 100}+ properties`;
    };

    const calculateEngagement = (views, inquiries) => {
        if (!views || views === 0) return 0;
        return ((inquiries || 0) / views * 100).toFixed(1);
    };

    const getScoreGrade = (score) => {
        if (score >= 9) return "Premium";
        if (score >= 8.5) return "Excellent";
        if (score >= 8) return "Great";
        if (score >= 7.5) return "Good";
        if (score >= 7) return "Fair";
        return "Average";
    };

    const getScoreColor = (score) => {
        if (score >= 9) return "text-jacaranda-600 bg-jacaranda-100";
        if (score >= 8.5) return "text-highland-500 bg-highland-50";
        if (score >= 8) return "text-highland-500 bg-highland-50";
        if (score >= 7.5) return "text-dawn-600 bg-dawn-100";
        if (score >= 7) return "text-murram-600 bg-murram-100";
        return "text-soil-600 bg-soil-50";
    };

// Pause auto-play on manual navigation
    const handleManualNavigation = (newIndex) => {
        setCurrentTestimonial(newIndex);
        setIsAutoPlay(false); // Stop auto-play when user manually navigates

        // Optional: Resume auto-play after 10 seconds of inactivity
        setTimeout(() => setIsAutoPlay(true), 10000);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/listings?search=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    const handlePropertyClick = (propertyId) => {
        navigate(`/property/${propertyId}`);
    };

    const handleLocationClick = (locationName) => {
        navigate(`/listings?location=${encodeURIComponent(locationName)}`);
    };

    return (
        <div className="min-h-screen bg-karibu-100">
            <SEO
                description="Find your perfect rental home anywhere in Kenya with Rent Utopia. Browse verified apartments, houses, and properties nationwide, from Nairobi and Mombasa to Kisumu and beyond."
                canonicalPath="/"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "Rent Utopia",
                    "url": "https://utopia.pegasus-pms.co.ke",
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "telephone": "+254-712-292-899",
                        "contactType": "Customer Service",
                        "email": "itorama254@gmail.com",
                        "availableLanguage": ["English", "Swahili"]
                    },
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": "Nairobi",
                        "addressCountry": "KE"
                    }
                }}
            />
            {/* Hero Section */}
            <section className="relative bg-highland-700 text-karibu-100 overflow-hidden">
                {/* Ambient doorway glow - the signature arch, softly lit from within */}
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] sm:w-[700px] h-[420px] sm:h-[520px] rounded-arch bg-gradient-to-b from-dawn-500/20 via-highland-500/10 to-transparent blur-2xl"
                />

                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 lg:pt-28 lg:pb-24">
                    <div className="text-center">
                        <span className="inline-block font-mono text-xs sm:text-sm tracking-[0.2em] uppercase text-dawn-400 mb-5">
                            Across Kenya
                        </span>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05] text-karibu-100">
                            Step through to
                            <span className="block text-dawn-400">your next home</span>
                        </h1>
                        <p className="text-lg lg:text-xl mb-10 max-w-2xl mx-auto text-karibu-200/80 leading-relaxed">
                            Thousands of verified rentals across Kenya, one doorway away.
                        </p>

                        {/* The doorway: search card with an arched threshold */}
                        <div className="max-w-2xl mx-auto mb-14">
                            <form onSubmit={handleSearch}>
                                <div className="bg-karibu-100 rounded-arch pt-12 pb-5 px-5 sm:px-7 shadow-2xl">
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <div className="flex-1 flex items-center bg-white rounded-xl px-4 py-3 border border-soil-100">
                                            <Search className="w-5 h-5 text-soil-400 mr-3 flex-shrink-0"/>
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                placeholder="Location, property type, or keywords..."
                                                className="w-full text-soil-900 text-base placeholder-soil-400 focus:outline-none bg-transparent"
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            className="bg-highland-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-highland-600 transition-colors duration-200 flex items-center justify-center"
                                        >
                                            <Search className="w-4 h-4 mr-2"/>
                                            Search
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-3xl mx-auto">
                            {isLoadingStats ? (
                                <div className="col-span-2 lg:col-span-4 flex justify-center">
                                    <Loader className="w-8 h-8 animate-spin text-dawn-400"/>
                                </div>
                            ) : (
                                stats.map((stat, index) => {
                                    const Icon = stat.icon;
                                    return (
                                        <div key={index} className="text-center">
                                            <Icon className="w-6 h-6 mx-auto mb-2 text-dawn-400"/>
                                            <div className="font-mono text-2xl font-bold text-karibu-100">{stat.value}</div>
                                            <div className="text-karibu-200/70 text-sm">{stat.label}</div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Properties */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-display text-3xl lg:text-5xl font-bold text-soil-900 mb-6">
                            Featured Properties
                        </h2>
                        <p className="text-xl text-soil-600 max-w-2xl mx-auto">
                            Hand-picked properties that offer the best value and experience
                        </p>
                    </div>

                    {isLoadingProperties ? (
                        <div className="flex justify-center py-12">
                            <div className="text-center">
                                <Loader className="w-12 h-12 text-highland-500 animate-spin mx-auto mb-4"/>
                                <p className="text-soil-600 text-lg">Loading featured properties...</p>
                            </div>
                        </div>
                    ) : featuredProperties.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {featuredProperties.map((property) => (
                                <PropertyCard
                                    key={property.id}
                                    property={property}
                                    onClick={() => handlePropertyClick(property.id)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-soil-50 rounded-2xl">
                            <Building className="w-12 h-12 text-soil-400 mx-auto mb-3"/>
                            <p className="text-soil-600 text-lg">No featured properties yet — check back soon.</p>
                        </div>
                    )}

                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/listings')}
                            className="bg-highland-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-highland-600 transition-all duration-200 inline-flex items-center"
                        >
                            View All Properties
                            <ArrowRight className="w-5 h-5 ml-2"/>
                        </button>
                    </div>
                </div>
            </section>

            {/* Popular Locations - Enhanced */}
            <section className="py-20 bg-soil-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-display text-3xl lg:text-5xl font-bold text-soil-900 mb-6">
                            Popular Locations
                        </h2>
                        <p className="text-xl text-soil-600 max-w-2xl mx-auto">
                            Explore the most sought-after neighborhoods in Nairobi
                        </p>
                    </div>

                    {isLoadingLocations ? (
                        <div className="flex justify-center py-12">
                            <div className="text-center">
                                <Loader className="w-12 h-12 text-highland-500 animate-spin mx-auto mb-4"/>
                                <p className="text-soil-600 text-lg">Loading popular locations...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {popularLocations.map((location, index) => (
                                <div
                                    key={location.neighborhoodId || index}
                                    onClick={() => handleLocationClick(location.name)}
                                    className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
                                >
                                    <div className="relative">
                                        <img
                                            src={location.image}
                                            alt={`${location.name}, ${location.city}`}
                                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                        <div
                                            className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"/>

                                        {/* Score Badge */}
                                        {location.overallScore && (
                                            <div className="absolute top-4 right-4">
                                                <div
                                                    className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(location.overallScore)}`}>
                                                    {location.overallScore}/10
                                                </div>
                                            </div>
                                        )}

                                        {/* Popularity Badge */}
                                        {location.popularityScore === 1 && (
                                            <div className="absolute top-4 left-4">
                                                <div
                                                    className="px-3 py-1 bg-dawn-500 text-white rounded-full text-sm font-bold flex items-center">
                                                    <Crown className="w-4 h-4 mr-1"/>
                                                    #1 Popular
                                                </div>
                                            </div>
                                        )}

                                        {/* Location Name Overlay */}
                                        <div className="absolute bottom-4 left-4 text-white">
                                            <h3 className="font-display text-2xl font-bold mb-1 group-hover:text-dawn-400 transition-colors duration-200">
                                                {location.name}
                                            </h3>
                                            <p className="text-white/90 flex items-center">
                                                <MapPin className="w-4 h-4 mr-1"/>
                                                {location.city}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="p-6">
                                        {/* Property Count */}
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center text-soil-600">
                                                <Building className="w-5 h-5 mr-2 text-murram-500"/>
                                                <span className="font-semibold">{location.properties}</span>
                                            </div>

                                            {/* Grade Badge */}
                                            {location.scoreGrade && (
                                                <div
                                                    className={`px-3 py-1 rounded-full text-xs font-bold ${getScoreColor(location.overallScore)}`}>
                                                    {location.scoreGrade}
                                                </div>
                                            )}
                                        </div>

                                        {/* Metrics */}
                                        <div className="grid grid-cols-2 gap-4 text-sm text-soil-600">
                                            {location.totalViews !== undefined && (
                                                <div className="flex items-center">
                                                    <Eye className="w-4 h-4 mr-2 text-soil-400"/>
                                                    <span>{location.totalViews.toLocaleString()} views</span>
                                                </div>
                                            )}

                                            {/* {location.totalInquiries !== undefined && (
                                                <div className="flex items-center">
                                                    <Users className="w-4 h-4 mr-2 text-soil-400"/>
                                                    <span>{location.totalInquiries} inquiries</span>
                                                </div>
                                            )}*/}
                                        </div>

                                        {/*  Engagement Rate
                                        {location.engagement && location.engagement > 0 && (
                                            <div className="mt-4 pt-4 border-t border-soil-100">
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-soil-600">Engagement Rate</span>
                                                    <span
                                                        className="font-bold text-highland-500">{location.engagement}%</span>
                                                </div>
                                            </div>
                                        )}*/}
                                    </div>

                                    {/* Hover Effect */}
                                    <div
                                        className="absolute inset-0 bg-highland-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"/>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Location Stats Summary */}
                    {popularLocations.length > 0 && !isLoadingLocations && (
                        <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg">
                            <h3 className="text-2xl font-bold text-soil-900 mb-6 text-center">
                                Location Insights
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-highland-500 mb-2">
                                        {popularLocations.length}
                                    </div>
                                    <div className="text-soil-600">Top Locations</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-highland-500 mb-2">
                                        {popularLocations.reduce((sum, loc) => sum + (loc.propertyCount || 0), 0).toLocaleString()}
                                    </div>
                                    <div className="text-soil-600">Total Properties</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-highland-500 mb-2">
                                        {(popularLocations.reduce((sum, loc) => sum + (loc.overallScore || 0), 0) / popularLocations.length).toFixed(1)}
                                    </div>
                                    <div className="text-soil-600">Avg. Score</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-highland-500 mb-2">
                                        {popularLocations.reduce((sum, loc) => sum + (loc.totalViews || 0), 0).toLocaleString()}
                                    </div>
                                    <div className="text-soil-600">Total Views</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* How It Works */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-display text-3xl lg:text-5xl font-bold text-soil-900 mb-6">
                            How It Works
                        </h2>
                        <p className="text-xl text-soil-600 max-w-2xl mx-auto">
                            Finding your next home has never been easier
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {howItWorks.map((step) => {
                            const Icon = step.icon;
                            return (
                                <div key={step.step} className="text-center group">
                                    <div className="relative mb-8">
                                        <div
                                            className="w-20 h-20 bg-highland-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                            <Icon className="w-10 h-10 text-white"/>
                                        </div>
                                        <div
                                            className="absolute -top-2 -right-2 w-8 h-8 bg-dawn-500 rounded-full flex items-center justify-center text-soil-900 font-bold text-sm font-mono">
                                            {step.step}
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-bold text-soil-900 mb-4">{step.title}</h3>
                                    <p className="text-soil-600 leading-relaxed">{step.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-20 bg-karibu-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-display text-3xl lg:text-5xl font-bold text-soil-900 mb-6">
                            What Our Users Say
                        </h2>
                        <p className="text-xl text-soil-600 max-w-2xl mx-auto">
                            Real stories from people who found their perfect home
                        </p>
                    </div>

                    {isLoadingTestimonials ? (
                        <div className="flex justify-center py-12">
                            <div className="text-center">
                                <Loader className="w-12 h-12 text-highland-500 animate-spin mx-auto mb-4"/>
                                <p className="text-soil-600 text-lg">Loading testimonials...</p>
                            </div>
                        </div>
                    ) : testimonials && testimonials.length > 0 ? (
                        <div className="relative max-w-5xl mx-auto">
                            {/* Single Testimonial Display */}
                            <div
                                className="bg-white p-8 lg:p-12 rounded-2xl shadow-xl min-h-[400px] flex flex-col justify-center">
                                {/* Rating Stars */}
                                <div className="flex justify-center mb-6">
                                    {[...Array(testimonials[currentTestimonial]?.rating || 5)].map((_, i) => (
                                        <Star key={i} className="w-6 h-6 text-dawn-500 fill-current mx-1"/>
                                    ))}
                                </div>

                                {/* Testimonial Content */}
                                <blockquote
                                    className="text-soil-600 text-lg lg:text-xl leading-relaxed text-center mb-8 font-medium">
                                    "{testimonials[currentTestimonial]?.content || 'Loading testimonial...'}"
                                </blockquote>

                                {/* User Info */}
                                <div className="text-center">
                                    <div className="font-bold text-soil-900 text-xl mb-2">
                                        {testimonials[currentTestimonial]?.user?.name || 'Anonymous User'}
                                    </div>
                                    <div className="text-soil-600 text-lg mb-4">
                                        {testimonials[currentTestimonial]?.user?.role ||
                                            testimonials[currentTestimonial]?.user?.occupation ||
                                            'Verified User'}
                                    </div>

                                    <div className="flex justify-center items-center space-x-4">
                                        {/* Property Info */}
                                        {testimonials[currentTestimonial]?.property && (
                                            <div
                                                className="inline-flex items-center px-4 py-2 bg-highland-50 text-highland-600 rounded-full text-sm">
                                                <MapPin className="w-4 h-4 mr-2"/>
                                                {testimonials[currentTestimonial].property.neighborhoodName}, {testimonials[currentTestimonial].property.city}
                                            </div>
                                        )}

                                        {/* Featured Badge */}
                                        {testimonials[currentTestimonial]?.isFeatured && (
                                            <div
                                                className="inline-flex items-center px-3 py-1 bg-jacaranda-500 text-white rounded-full text-sm font-medium">
                                                <Crown className="w-4 h-4 mr-1"/>
                                                Featured
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Navigation Controls */}
                            {testimonials.length > 1 && (
                                <>
                                    {/* Previous Button */}
                                    <button
                                        onClick={() => {
                                            setCurrentTestimonial(prev =>
                                                prev === 0 ? testimonials.length - 1 : prev - 1
                                            );
                                        }}
                                        className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-highland-50 group z-10"
                                        aria-label="Previous testimonial"
                                    >
                                        <ChevronLeft className="w-6 h-6 text-soil-600 group-hover:text-highland-500"/>
                                    </button>

                                    {/* Next Button */}
                                    <button
                                        onClick={() => {
                                            setCurrentTestimonial(prev =>
                                                prev === testimonials.length - 1 ? 0 : prev + 1
                                            );
                                        }}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-highland-50 group z-10"
                                        aria-label="Next testimonial"
                                    >
                                        <ChevronRight className="w-6 h-6 text-soil-600 group-hover:text-highland-500"/>
                                    </button>

                                    {/* Dots Indicator */}
                                    <div className="flex justify-center mt-8 space-x-2">
                                        {testimonials.map((_, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setCurrentTestimonial(index)}
                                                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                                    index === currentTestimonial
                                                        ? 'bg-highland-500 w-8'
                                                        : 'bg-soil-100 hover:bg-soil-400'
                                                }`}
                                                aria-label={`Go to testimonial ${index + 1}`}
                                            />
                                        ))}
                                    </div>

                                    {/* Testimonial Counter */}
                                    <div className="text-center mt-4 text-soil-400 text-sm">
                                        {currentTestimonial + 1} of {testimonials.length}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-soil-600 text-lg">No testimonials available at the moment.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Call to Action */}
            <section
                className="py-20 bg-highland-700 text-karibu-100 relative overflow-hidden">
                {/* Ambient doorway glow, echoing the hero */}
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[360px] rounded-arch bg-gradient-to-b from-dawn-500/15 to-transparent blur-2xl"
                />

                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-display text-3xl lg:text-5xl font-bold mb-6">
                        Ready to find your dream home?
                    </h2>
                    <p className="text-xl lg:text-2xl mb-12 text-karibu-200/80 leading-relaxed">
                        Join thousands of satisfied tenants who found their perfect home through Rent Utopia
                    </p>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <button
                            onClick={() => navigate('/listings')}
                            className="group bg-karibu-100 text-highland-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl"
                        >
                            Browse Properties
                            <ArrowRight
                                className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200"/>
                        </button>
                        <button
                            onClick={() => navigate('/register')}
                            className="group border-2 border-karibu-100 text-karibu-100 px-8 py-4 rounded-xl font-bold text-lg hover:bg-karibu-100 hover:text-highland-700 transition-all duration-200 flex items-center justify-center"
                        >
                            <UserPlus className="mr-3 w-5 h-5"/>
                            Sign Up Free
                        </button>
                    </div>

                    {/* Trust Indicators */}
                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 opacity-90">
                        <div className="text-center">
                            <Shield className="w-8 h-8 mx-auto mb-2 text-dawn-400"/>
                            <div className="text-sm text-karibu-200/80">Verified Properties</div>
                        </div>
                        <div className="text-center">
                            <Users className="w-8 h-8 mx-auto mb-2 text-dawn-400"/>
                            <div className="text-sm text-karibu-200/80">Trusted Community</div>
                        </div>
                        <div className="text-center">
                            <Star className="w-8 h-8 mx-auto mb-2 text-dawn-400"/>
                            <div className="text-sm text-karibu-200/80">5-Star Support</div>
                        </div>
                        <div className="text-center">
                            <Building className="w-8 h-8 mx-auto mb-2 text-dawn-400"/>
                            <div className="text-sm text-karibu-200/80">Premium Listings</div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;